'use client';

import { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Box, CssBaseline, Typography } from '@mui/material';
import SocialLinks from '@components/SocialLinks';
import { ThemeProvider } from '@mui/material/styles';
import theme from '@styles/theme';

export default function HomeLayout({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <title>Welcome to My Journey | Cdaprod Blog</title>
        <meta
          name="description"
          content="Explore my projects, technical insights, and personal stories about resilience and innovation."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </head>
      <body>
        <ThemeProvider theme={theme}>
          <CssBaseline />

          {/* Animated Gradient Background */}
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: -1,
              background: (theme) => `
                radial-gradient(circle at 10% 20%, ${theme.palette.primary.main}15, transparent 80%),
                radial-gradient(circle at 90% 80%, ${theme.palette.secondary.main}15, transparent 80%),
                radial-gradient(circle at 50% 50%, ${theme.palette.info.main}15, transparent 80%)
              `,
              opacity: 0.9,
              filter: 'blur(50px)',
              [theme.breakpoints.down('sm')]: {
                filter: 'blur(30px)',
              },
            }}
            aria-hidden="true"
          />

          {/* Parallax Elements */}
          <Box
            component={motion.div}
            animate={{ y: [0, 20, 0], scale: [1, 1.05, 1] }}
            transition={{ duration: 30, repeat: Infinity, ease: 'easeInOut' }}
            sx={{
              position: 'fixed',
              top: '10%',
              left: '5%',
              width: '200px',
              height: '200px',
              background: (theme) => theme.palette.primary.light,
              opacity: 0.1,
              borderRadius: '50%',
              filter: 'blur(100px)',
              zIndex: -2,
            }}
            aria-hidden="true"
          />
          <Box
            component={motion.div}
            animate={{ y: [0, -20, 0], scale: [1.1, 1, 1.1] }}
            transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
            sx={{
              position: 'fixed',
              bottom: '15%',
              right: '10%',
              width: '300px',
              height: '300px',
              background: (theme) => theme.palette.secondary.light,
              opacity: 0.1,
              borderRadius: '50%',
              filter: 'blur(120px)',
              zIndex: -2,
            }}
            aria-hidden="true"
          />

          {/* Main Content Area */}
          <Box
            sx={{
              minHeight: '100vh',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <Box
              component="main"
              sx={{
                px: { xs: 2, sm: 4 },
                py: { xs: 4, sm: 6 },
                borderRadius: '12px',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
              }}
            >
              {children}
            </Box>

            {/* Footer with Social Links */}
            <footer className="bg-gray-900 text-white py-4 text-center">
              <SocialLinks />
              <Typography sx={{ mt: 2 }}>
                © {new Date().getFullYear()} Cdaprod Blog - All Rights Reserved
              </Typography>
            </footer>
          </Box>
        </ThemeProvider>
      </body>
    </html>
  );
}